import { MotionValue, useMotionValue } from "framer-motion";
import { configureI18n } from "@assets/strings";
import { identity } from "lodash";
import clamp from "lodash/clamp";
import { Observable, useMemoizedObservable, useObservable } from "micro-observables";
import { DependencyList, EffectCallback, RefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { useLocation } from "react-router";
import { BrowserLocalStorage } from "@core/localStorage/storage";
import { PaginatedData } from "@core/paginatedData";
import { ContextStore } from "@core/store/contextStore";
import { MappedStore } from "@core/store/mappedStore";
import { PaginatedStore } from "@core/store/paginatedStore";
import { Store } from "@core/store/store";
import { Application } from "@domain/application/application";
import { ApplicationVoteDto } from "@domain/application/dtos";
import { CurrentProfile, Language } from "@domain/currentProfile/currentProfile";
import { DEFAULT_HEIGHT, DEFAULT_WIDTH } from "@ui/styles/utils";
import { isDayHasPassed } from "./date";
import { Page } from "./page";

interface InitialResult {
	error: null;
	loading: false;
	result: null;
}
interface PendingResult {
	error: null;
	loading: true;
	result: null;
}
interface SuccessResult<T> {
	error: null;
	loading: false;
	result: T;
}
interface PromiseError extends Error {
	code: string;
}
interface ErrorResult {
	error: PromiseError;
	loading: false;
	result: null;
}
export type PromiseResult<T> = InitialResult | PendingResult | SuccessResult<T> | ErrorResult;

export function resolvedPromiseResult<T>(value: T): PromiseResult<T> {
	return {
		result: value,
		loading: false,
		error: null,
	};
}

export function useAsync<T, Args extends unknown[]>(
	asyncTask: (...args: Args) => Promise<T>,
	deps: unknown[]
): PromiseResult<T> & { run: (...args: Args) => Promise<T> } {
	const [promiseResult, setPromiseResult] = useState<PromiseResult<T>>({
		error: null,
		loading: false,
		result: null,
	});

	const run = useCallback(async (...args: Args) => {
		setPromiseResult({ result: null, loading: true, error: null });
		try {
			const result = await asyncTask(...args);
			setPromiseResult({ result, loading: false, error: null });
			return result;
		} catch (e) {
			console.error(e);
			setPromiseResult({ result: null, loading: false, error: e as PromiseError });
			throw e;
		}
	}, deps);

	return { run, ...promiseResult };
}

export function usePromise<T>(asyncTask: () => Promise<T>, deps: unknown[]): PromiseResult<T> {
	const defaultResult: PromiseResult<T> = {
		error: null,
		loading: true,
		result: null,
	};
	const sync = useStateIsSync(deps);
	const promiseResult = useAsync(asyncTask, deps);

	useEffect(() => {
		promiseResult.run();
	}, deps);
	return sync ? promiseResult : defaultResult;
}

export type PageResult<T, Extra = unknown> = {
	error: PromiseError | null;
	loading: boolean;
	result: T[];
	totalPages?: number;
	totalElements?: number;
} & Extra;
export function usePaginated<T>(
	asyncTask: (page: number) => Promise<Page<T>>,
	page: number,
	deps: unknown[]
): PageResult<T> {
	const { result, error, loading } = usePromise(() => asyncTask(page), [page, ...deps]);
	const [currentResults, setCurrentResults] = useState<T[]>(result ? [...result.content] : []);
	const totalPages = result?.total_pages;
	const totalElements = result?.total_size;
	const sync = useStateIsSync(deps);

	useEffect(() => {
		setCurrentResults([]);
	}, deps);

	useEffect(() => {
		if (result) {
			if (page === 0) {
				setCurrentResults([...result.content]);
			} else {
				setCurrentResults((current) => [...current, ...result.content]);
			}
		}
	}, [result]);

	return {
		result: sync ? currentResults : [],
		error,
		loading: sync ? loading : true,
		totalElements,
		totalPages,
	};
}

export interface PaginatedDataResult<T, Extra = unknown> extends PageResult<T> {
	fetch: () => Promise<void>;
	fetchMore: () => Promise<void>;
	moreLoading: boolean;
	extras?: Extra;
	lastPage: boolean;
}
export function emptyPaginatedDataResult<T, Extra = unknown>(): PaginatedDataResult<T, Extra> {
	return {
		result: [],
		loading: false,
		error: null,
		totalPages: 0,
		totalElements: 0,
		fetch: async () => {},
		fetchMore: async () => {},
		moreLoading: false,
		lastPage: false,
	};
}
export function useObservablePaginatedData<T, Extra>(
	observableData: Observable<PaginatedData<T, Extra> | null>,
	fetch: () => Promise<void>,
	fetchMoreData: () => Promise<void>,
	deps: unknown[]
): PaginatedDataResult<T, Extra> {
	const data = useMemoizedObservable(() => observableData, deps);
	const { error, loading } = usePromise(() => fetch(), deps);
	const result = data?.content.toArray() ?? [];
	const totalPages = data?.total_pages;
	const totalElements = data?.total_size;

	const [moreLoading, setMoreLoading] = useState(false);
	const fetchMore = useCallback(async () => {
		if (loading || moreLoading || !totalPages || !data || totalPages <= data.page + 1) {
			return;
		}

		setMoreLoading(true);
		await fetchMoreData();
		setMoreLoading(false);
	}, [loading, moreLoading, totalPages, data, ...deps]);

	const lastPage = totalPages !== undefined && data !== null && data.page >= totalPages - 1;

	return {
		extras: data ?? undefined,
		result,
		totalPages,
		totalElements,
		loading,
		moreLoading,
		error,
		lastPage,
		fetch,
		fetchMore,
	};
}

export function useObservablePaginatedDataDirect<T, Extra>(
	observableData: Observable<PaginatedData<T, Extra> | null>,
	fetch: () => Promise<void>,
	fetchMoreData: () => Promise<void>,
	deps: unknown[]
): PaginatedDataResult<T, Extra> {
	const data = useMemoizedObservable(() => observableData, deps);
	const { error, loading } = usePromise(() => fetch(), deps);

	const result = data?.content.toArray() ?? [];
	const totalPages = data?.total_pages;
	const totalElements = data?.total_size;

	const [moreLoading, setMoreLoading] = useState(false);
	const fetchMore = useCallback(async () => {
		if (loading || moreLoading || !totalPages || !data || totalPages <= data.page + 1) {
			return;
		}

		setMoreLoading(true);
		await fetchMoreData();
		setMoreLoading(false);
	}, [loading, moreLoading, totalPages, data, ...deps]);

	const lastPage = totalPages !== undefined && data !== null && data.page >= totalPages - 1;

	return {
		extras: data ?? undefined,
		result,
		totalPages,
		totalElements,
		loading,
		moreLoading,
		error,
		lastPage,
		fetch,
		fetchMore,
	};
}

export function useClickOutside(callback: (e: MouseEvent) => void): React.RefObject<HTMLDivElement | null> {
	const ref = useRef<HTMLDivElement | null>(null);
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent): void => {
			if (ref.current && !ref.current.contains(event.target as Node | null)) {
				callback(event);
			}
		};
		document.addEventListener("click", handleClickOutside, true);
		return () => document.removeEventListener("click", handleClickOutside, true);
	}, [ref, callback]);

	return ref;
}

export function useStateIsSync(deps: unknown[]): boolean {
	const syncCount = useRef(0);
	const renderWithDepsCount = useMemo(() => syncCount.current + 1, deps);

	useEffect(() => {
		syncCount.current++;
	}, deps);

	return syncCount.current === renderWithDepsCount;
}

export function useOutsideAlerter(ref: RefObject<HTMLElement | null>, cb: () => void): void {
	useEffect(() => {
		/**
		 * Alert if clicked on outside of element
		 */
		// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
		function handleClickOutside(event: MouseEvent) {
			if (ref.current && event.target instanceof Node && !ref.current.contains(event.target)) {
				cb();
			}
		}
		// Bind the event listener
		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			// Unbind the event listener on clean up
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [ref]);
}

export function useChangeEffect(effect: EffectCallback, deps?: DependencyList): void {
	const firstRender = useRef(true);

	useEffect(() => {
		if (firstRender.current) {
			firstRender.current = false;
		} else {
			return effect();
		}
	}, deps);
}

export function useIsMobile(): boolean {
	return useMediaQuery({
		query: "(max-width: 900px)",
	});
}

export interface AsyncResult<T> {
	result: T | null;
	loading: boolean;
	error: (Error & { code?: number }) | null;
}

export function resolvedAsyncResult<T>(value: T): AsyncResult<T> {
	return {
		result: value,
		loading: false,
		error: null,
	};
}

export function useStoredData<T extends { id: string }>(id: string, store: Store<T>, deps: unknown[]): AsyncResult<T> {
	const result = useMemoizedObservable(() => store.getObservable(id), [id]);

	const [loading, setLoading] = useState(!result);
	const [error, setError] = useState(null);

	useEffect(() => {
		setLoading(true);
		store
			.fetch(id)
			.catch((e) => setError(e))
			.finally(() => setLoading(false));
	}, [id, ...deps]);

	return { result, loading, error };
}

export function usePaginatedStore<T, Extras = unknown>(
	paginatedStore: PaginatedStore<T, Extras, []>,
	deps: unknown[]
): PaginatedDataResult<T, Extras> {
	return useObservablePaginatedData(
		paginatedStore.paginatedItems,
		() => paginatedStore.fetch(),
		() => paginatedStore.fetchMore(),
		deps
	);
}

// TODO Maybe this should be the default one ?
export function usePaginatedStoreWithArgs<T, Extras, Args extends unknown[]>(
	paginatedStore: PaginatedStore<T, Extras, Args>,
	deps: unknown[],
	...args: Args
): PaginatedDataResult<T, Extras> {
	if (!paginatedStore) {
		console.error("No Store Detected");
	}
	const loading = useObservable(paginatedStore.fetching);
	const result = useObservablePaginatedData(
		paginatedStore.paginatedItems,
		() => paginatedStore.fetch(...args),
		() => paginatedStore.fetchMore(...args),
		[...deps, ...args]
	);

	return {
		...result,
		loading: result.loading || loading,
	};
}

export function usePaginatedStoreWithArgsDirect<T, Extras, Args extends unknown[]>(
	paginatedStore: PaginatedStore<T, Extras, Args>,
	deps: unknown[],
	...args: Args
): PaginatedDataResult<T, Extras> {
	const loading = useObservable(paginatedStore.fetching);
	const result = useObservablePaginatedDataDirect(
		paginatedStore.paginatedItems,
		() => paginatedStore.fetch(...args),
		() => paginatedStore.fetchMore(...args),
		[...deps, ...args]
	);

	return {
		...result,
		loading: result.loading || loading,
	};
}

export function useMappedStore<T, S>(id: S, mappedStore: MappedStore<T, S>, deps: unknown[]): PaginatedDataResult<T> {
	return useObservablePaginatedData(
		mappedStore.getObservable(id),
		() => mappedStore.fetch(id),
		() => mappedStore.fetchMore(id),
		deps
	);
}

export function useContextStore<T extends U, U extends { id: string }, S extends string>(
	context: S,
	id: string,
	store: ContextStore<T, U, S>
): AsyncResult<T> {
	const result = useMemoizedObservable(() => store.getOne(context, id), [context, id]);
	const [loading, setLoading] = useState(!result);
	const [error, setError] = useState(null);

	useEffect(() => {
		setLoading(true);
		store
			.fetchOne(context, id)
			.catch((e) => setError(e))
			.finally(() => setLoading(false));
	}, [context, id]);

	return { result, loading, error };
}

export function useLockBodyScroll(): void {
	useEffect(() => {
		const originalStyle = window.getComputedStyle(document.body).overflow;
		document.body.style.overflow = "hidden";
		return () => {
			if (originalStyle !== "hidden") {
				document.body.style.overflow = originalStyle;
			}
		};
	}, []);
}

export function useEffectAsync(effect: () => Promise<unknown>, deps: unknown[]): void {
	const effectMemo = useCallback(effect, deps);
	useEffect(() => {
		effectMemo();
	}, deps);
}

export function useIsMounted(): boolean {
	const [mounted, setMounted] = useState(false);
	useEffect(() => {
		setMounted(true);
	}, []);
	return mounted;
}

export function useUnmount<T extends unknown[]>(effect: (deps: readonly [...T]) => void, deps: readonly [...T]): void {
	const depRefs = useRef(deps);
	useEffect(() => {
		depRefs.current = deps;
	}, deps);
	useEffect(() => () => effect(depRefs.current), []);
}

export function useScrollToHash(topGap?: number, deps = []): void {
	const location = useLocation();
	const { hash } = location;

	useEffect(() => {
		if (hash) {
			document.getElementById(hash.slice(1))?.scrollIntoView();
			if (topGap) {
				window.scrollBy(0, -topGap);
			}
		}
	}, [...deps, location]);
}

export function useScrollDelta(maxOffset: number): MotionValue<number> {
	const lastScroll = useRef(0);
	const directionDelta = useMotionValue(0);

	useEffect(() => {
		lastScroll.current = window.scrollY;
		const listener = (): void => {
			if (window.scrollY < 50) {
				directionDelta.set(0);
				return;
			}
			const delta = window.scrollY - lastScroll.current;
			directionDelta.set(clamp(directionDelta.get() + delta, 0, maxOffset));
			lastScroll.current = window.scrollY;
		};
		document.addEventListener("scroll", listener);
		return () => document.removeEventListener("scroll", listener);
	}, []);

	return directionDelta;
}

function readWindowSize(): { width: number; height: number } {
	if (typeof window === "undefined") {
		return { width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT };
	}
	return { width: window.innerWidth, height: window.innerHeight };
}

export function useWindowSize(): {
	width: number;
	height: number;
} {
	const [size, setSize] = useState(readWindowSize);
	useEffect(() => {
		const resizeListener = (): void => {
			setSize((current) => {
				const next = readWindowSize();
				if (current.width === next.width && current.height === next.height) {
					return current;
				}
				return next;
			});
		};
		resizeListener();
		window.addEventListener("resize", resizeListener);
		window.addEventListener("orientationchange", resizeListener);
		window.visualViewport?.addEventListener("resize", resizeListener);
		const frame = window.requestAnimationFrame(resizeListener);
		// Installed PWAs and in-app browsers often correct the viewport just after the first paint, without a resize event.
		const timers = [100, 400].map((delay) => window.setTimeout(resizeListener, delay));
		return () => {
			window.removeEventListener("resize", resizeListener);
			window.removeEventListener("orientationchange", resizeListener);
			window.visualViewport?.removeEventListener("resize", resizeListener);
			window.cancelAnimationFrame(frame);
			timers.forEach((timer) => window.clearTimeout(timer));
		};
	}, []);

	return size;
}

export function useLanguageFromProfile(language?: Language): void {
	useEffect(() => {
		if (language) {
			const code = language === "FRENCH" ? "fr" : "en";
			configureI18n(code);
		}
	}, [language]);
}

export function useOneCallTimer(
	timeInMilliseconds: number,
	cb: (() => Promise<void>) | undefined,
	deps: DependencyList | undefined
): void {
	useEffect(() => {
		const timeout = setTimeout(() => {
			if (cb) {
				void cb();
			}
		}, timeInMilliseconds);
		// This represents the unmount function, in which you need to clear your interval to prevent memory leaks.
		return () => clearTimeout(timeout);
	}, deps);
}

export function useHover(): { ref: RefObject<HTMLDivElement | null>; isHovered: boolean } {
	const [isHovered, setIsHovered] = useState(false);
	const ref = useRef<HTMLDivElement>(null);

	const handleMouseOver = (): void => setIsHovered(true);
	const handleMouseLeave = (): void => setIsHovered(false);

	useEffect(() => {
		const node = ref.current;

		if (node) {
			node.addEventListener("mouseover", handleMouseOver);
			node.addEventListener("mouseleave", handleMouseLeave);
		}

		return () => {
			node?.removeEventListener("mouseover", handleMouseOver);
			node?.removeEventListener("mouseleave", handleMouseLeave);
		};
	}, [ref.current]);

	return { ref, isHovered };
}

export function useVoteOnLocalStorage(
	profileSlug: string,
	currentProfile: CurrentProfile | null,
	application: Application | null
): { hasVoted: boolean; voteCount: number; lastVote: { key: string; date: Date } | null; vote: () => void } {
	const votesStorage = new BrowserLocalStorage<ApplicationVoteDto | null>("votes", identity, identity, "local");
	const votesOnLocalStorage = votesStorage?.getItem();

	const [lastVote, setLastVote] = useState<{ key: string; date: Date } | null>(null);
	const [hasVoted, setHasVoted] = useState(false);
	const [voteCount, setVoteCount] = useState(0);

	useEffect(() => {
		if (!currentProfile) {
			if (!votesOnLocalStorage) {
				setHasVoted(false);
			} else {
				const isVotedForProfile = votesOnLocalStorage[profileSlug];
				if (isVotedForProfile) {
					setHasVoted(isDayHasPassed(isVotedForProfile.date));
					setLastVote(isVotedForProfile);
				} else {
					setHasVoted(false);
				}
			}
		} else {
			if (votesOnLocalStorage) {
				const isVotedForProfile = votesOnLocalStorage[profileSlug];
				if (isVotedForProfile) {
					setHasVoted(isDayHasPassed(isVotedForProfile.date));
					setLastVote(isVotedForProfile);
				} else {
					setHasVoted(false);
				}
			} else {
				if (application) setHasVoted(application.hasAlreadyVoted);
			}
		}

		if (application) setVoteCount(application.voteCount);

		return;
	}, [currentProfile?.id, application]);

	const vote = useCallback(() => {
		setHasVoted(!hasVoted);
		setVoteCount((voteCount) => voteCount + 1);
	}, [hasVoted, voteCount]);

	return { hasVoted, lastVote, voteCount, vote };
}

export function useInterval(callback: () => void, delay: number): void {
	const savedCallback = useRef<() => void>(null);

	useEffect(() => {
		savedCallback.current = callback;
	}, [callback]);

	useEffect(() => {
		function tick(): void {
			savedCallback.current?.();
		}

		if (delay === null) {
			return;
		}

		const id = setInterval(tick, delay);
		return () => clearInterval(id);
	}, [delay]);
}

export const useTimer = (options: {
	duration: number;
	start: boolean;
	maxAttempts?: number;
}): {
	timer: number;
	resetTimer: () => void;
	isMaxAttemptsReached: boolean;
} => {
	const { duration, start = false, maxAttempts = 5 } = options;
	const [timer, setTimer] = useState(duration);
	const [attempts, setAttempts] = useState(0);
	let intervalId: NodeJS.Timeout;

	useEffect(() => {
		const startTimer = (): void => {
			intervalId = setInterval(() => {
				setTimer((prevTimer) => {
					if (prevTimer === 0) {
						resetTimer();
					}
					return prevTimer - 1;
				});
			}, 1000);
		};

		if (start) {
			startTimer();
		}

		return () => {
			clearInterval(intervalId);
		};
	}, [start]);

	useEffect(() => {
		if (timer === 0) {
			clearInterval(intervalId);
		}
	}, [timer]);

	const resetTimer = (): void => {
		setAttempts((prevAttempts) => prevAttempts + 1);
		setTimer(duration);
	};

	const isMaxAttemptsReached = attempts >= maxAttempts;

	return {
		timer,
		resetTimer,
		isMaxAttemptsReached,
	};
};
